import { browser, version } from "$app/env";
import type { Notification as AppNotification } from "#lib/api-client/types.gen.js";
import { isSafeActionUrl } from "./action-url";

/** Per-user setting that remembers whether desktop notifications are enabled. */
export const DESKTOP_NOTIFICATION_SETTING = "notifications.desktop";

const SERVICE_WORKER_URL = "/sw.js";
const NOTIFICATION_ICON = "/favicon.svg";

/**
 * Each build registers its own worker URL. This is what makes deploys safe
 * without a per-file cache policy: the CDN can cache `/sw.js` aggressively, but
 * a new app version asks for `/sw.js?v=<version>`, which is a new cache key, so
 * a stale worker can never be pinned for the new build.
 */
function serviceWorkerURL(): string {
	return `${SERVICE_WORKER_URL}?v=${encodeURIComponent(version)}`;
}

export type DesktopPermission = NotificationPermission | "unsupported";

type NotificationCtor = typeof Notification;

/**
 * The Web Notifications constructor, or null outside a browser that supports it
 * (SSR, jsdom, older mobile browsers that only expose service-worker
 * notifications).
 */
function notificationCtor(): NotificationCtor | null {
	if (!browser || typeof window === "undefined") return null;
	return (window as unknown as { Notification?: NotificationCtor }).Notification ?? null;
}

export function desktopNotificationsSupported(): boolean {
	return notificationCtor() !== null;
}

export function desktopNotificationPermission(): DesktopPermission {
	const ctor = notificationCtor();
	if (!ctor) return "unsupported";
	return ctor.permission;
}

/**
 * Asks the browser for notification permission. Must be called from a user
 * gesture: browsers reject (and may permanently deny) unsolicited prompts.
 */
export async function requestDesktopNotificationPermission(): Promise<DesktopPermission> {
	const ctor = notificationCtor();
	if (!ctor) return "unsupported";
	if (ctor.permission !== "default") return ctor.permission;
	try {
		return await ctor.requestPermission();
	} catch {
		return ctor.permission;
	}
}

/**
 * True when the app is not the active window/tab — the condition for handing a
 * notification to the OS instead of showing an in-app toast.
 */
export function shouldPresentDesktopNotification(): boolean {
	if (!browser || typeof document === "undefined") return false;
	if (document.visibilityState === "hidden") return true;
	return typeof document.hasFocus === "function" ? !document.hasFocus() : false;
}

let workerRegistration: Promise<ServiceWorkerRegistration | null> | null = null;

/**
 * Registers the tiny notification service worker (no caching — it only renders
 * notifications and handles clicks). Resolves null when service workers are
 * unavailable so callers can fall back to a page-created Notification.
 */
export function registerNotificationWorker(): Promise<ServiceWorkerRegistration | null> {
	if (!browser || typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
		return Promise.resolve(null);
	}
	workerRegistration ??= navigator.serviceWorker
		.register(serviceWorkerURL(), { scope: "/" })
		.then(async () => {
			try {
				// The registration is only usable once a worker is active; bound the
				// wait so a bad worker can never hang notification delivery.
				return await Promise.race([
					navigator.serviceWorker.ready,
					new Promise<null>((resolve) => setTimeout(() => resolve(null), 5_000)),
				]);
			} catch {
				return null;
			}
		})
		.catch(() => null);
	return workerRegistration;
}

/**
 * Sends a notification to the host OS through the service worker (falling back
 * to a page-owned Notification when the worker is unavailable). Best effort:
 * anything that goes wrong is swallowed — notifications must never break the
 * realtime feed.
 */
export async function showDesktopNotification(notification: AppNotification): Promise<void> {
	const ctor = notificationCtor();
	if (!ctor || ctor.permission !== "granted") return;

	const url = isSafeActionUrl(notification.action_url) ? (notification.action_url as string) : "/";
	const options: NotificationOptions = {
		body: notification.message,
		icon: NOTIFICATION_ICON,
		badge: NOTIFICATION_ICON,
		tag: notification.id,
		data: { url },
	};

	try {
		const registration = await registerNotificationWorker();
		if (registration) {
			await registration.showNotification(notification.title, options);
			return;
		}
	} catch {
		// Fall through to the constructor-based notification.
	}

	try {
		const fallback = new ctor(notification.title, options);
		fallback.onclick = () => {
			fallback.close();
			window.focus();
			window.location.assign(url);
		};
	} catch {
		// Desktop notifications are a nicety; never let one break delivery.
	}
}
